import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import ExcelTable from 'components/ui/excel-table';
import {
  AllRowsData,
  ECellValueType,
  EExcelTableColumnType,
  ECellValueDisplayMode,
  RowData,
} from 'components/ui/excel-table/types';
import { validateDeclarationDetails } from 'components/features/binding/contract-detail/declaration-detail/form';
import { FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import {
  ContractResponseDtoContractStatusEnum,
  LayerResponseDtoLimitTypeEnum,
} from 'libs/pinnacle-sdk';
import { ValueGetterParams } from 'ag-grid-community';
import { isNil } from 'lodash';
import { countryStateCityApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';

export interface DeclarationData {
  id: number;
  declarationName: string;
  entityName: string;
  country: string;
  programOrder: number;
  programOrderTotalPremium: number;
  shareOfPremiumSigned: number;
  declarationId?: number;
  premiumScheduleId?: string;
  dueDate?: Dayjs;
}

interface DeclarationDetailsSectionProps {
  form: FormInstance;
  declarationData: DeclarationData[];
  setDeclarationData: (declarationData: DeclarationData[]) => void;
  setIsDeclarationValid: (isDeclarationValid: boolean) => void;
  disabled?: boolean;
  layerPremium: number;
  contractStatus?: ContractResponseDtoContractStatusEnum;
}

const DeclarationDetailsSection: React.FC<DeclarationDetailsSectionProps> = ({
  form,
  declarationData,
  setDeclarationData,
  setIsDeclarationValid,
  disabled = false,
  layerPremium,
  contractStatus,
}) => {
  const binderShareSigned = useWatch('binderShareSigned', form);

  const { t } = useTranslation();

  const declarationDataLengthRef = useRef(declarationData.length);

  useEffect(() => {
    declarationDataLengthRef.current = declarationData.length;
  }, [declarationData.length]);

  const { data: countries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: t('contractDetail.declarationDetail.declarationName'),
        field: 'declarationName',
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t('contractDetail.declarationDetail.entityName'),
        field: 'entityName',
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t('contractDetail.declarationDetail.country'),
        field: 'country',
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions:
          countries?.map((country) => ({
            value: country.name,
            label: country.name,
          })) || [],
        resizable: true,
      },
      {
        headerName: t('contractDetail.declarationDetail.programOrder'),
        field: 'programOrder',
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        precision: 2,
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t(
          'contractDetail.declarationDetail.programOrderTotalPremium'
        ),
        field: 'programOrderTotalPremium',
        type: ECellValueType.NUMERIC_COLUMN,
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t('contractDetail.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
      },
      {
        headerName: t('contractDetail.declarationDetail.declarationDueDate'),
        field: 'dueDate',
        type: ECellValueType.DATE_COLUMN,
        editable: !disabled,
        minWidth: 175,
        flex: 1,
        sortable: false,
        resizable: true,
        disabledDate: (current: Dayjs) => {
          const today = dayjs().startOf('day');
          return current < today;
        },
      },
    ],
    [t, disabled, countries]
  );

  const newRowData = useMemo(
    () => ({
      declarationName: '',
      entityName: '',
      country: '',
      programOrder: 0,
      programOrderTotalPremium: 0,
      shareOfPremiumSigned: 0,
      dueDate: dayjs(),
    }),
    []
  );

  const onDataChange = useCallback(
    async (
      affectedRow: RowData<DeclarationData>,
      rows: AllRowsData<DeclarationData>
    ) => {
      // Update ref with current data length
      declarationDataLengthRef.current = rows.data.length;

      setDeclarationData(rows.data);
    },
    [setDeclarationData]
  );

  const onDeleteRow = useCallback(
    (
      affectedRow: RowData<DeclarationData>,
      rows: AllRowsData<DeclarationData>
    ) => {
      declarationDataLengthRef.current = rows.data.length;

      if (rows.data.length <= 1) {
        return;
      }

      onDataChange(affectedRow, rows);
    },
    [onDataChange]
  );

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) => {
      if (!isError.soft && !isError.strict) {
        setIsDeclarationValid(true);
      } else {
        setIsDeclarationValid(false);
      }
    },
    [setIsDeclarationValid]
  );

  const canDeleteRow = (row: DeclarationData) => {
    return declarationDataLengthRef.current > 1;
  };

  const canAddRow = useCallback((tableData: DeclarationData[]) => {
    return {
      canAdd: true,
      message: undefined,
    };
  }, []);

  const onNewRow = useCallback(
    (
      affectedRow: RowData<DeclarationData>,
      rows: AllRowsData<DeclarationData>
    ) => {
      declarationDataLengthRef.current = rows.data.length;

      onDataChange(affectedRow, rows);
    },
    [onDataChange]
  );

  const shouldShowDeleteColumn = useMemo(() => {
    return !disabled;
  }, [disabled]);

  const declarationDataWithShareOfPremiumSigned = useMemo(() => {
    if (declarationData.length === 0 || isNil(binderShareSigned)) {
      return declarationData;
    }

    return declarationData.map((row) => ({
      ...row,
      shareOfPremiumSigned: !isNil(row.programOrderTotalPremium)
        ? (row.programOrderTotalPremium * binderShareSigned) / 100
        : 0,
    }));
  }, [declarationData, binderShareSigned]);

  return (
    <FormSection title={t('contractDetail.declarationDetails')} collapsible>
      <FormLayoutGroup mdColumns={1}>
        <ExcelTable
          data={declarationDataWithShareOfPremiumSigned}
          columnDefs={columnDefs}
          rowKeyColName="id"
          includeIdRow
          includeAddRow={!disabled}
          includeDeleteCol={shouldShowDeleteColumn}
          tableContainerClassName="h-[20rem]"
          newRowData={newRowData}
          validateRow={validateDeclarationDetails(layerPremium, contractStatus)}
          canAddRow={canAddRow}
          onDataChange={onDataChange}
          onNewRow={onNewRow}
          onErrorChange={onErrorChange}
          onDeleteRow={onDeleteRow}
          canDeleteRow={canDeleteRow}
        />
      </FormLayoutGroup>
    </FormSection>
  );
};

export default DeclarationDetailsSection;
